import { useEffect, useMemo } from 'react';

import { QueryFunctionContext, QueryKey, useQueries, useQueryClient } from '@tanstack/react-query';

import { getErrorKind } from '@/shared/lib/errors';

import { isDriveUnavailable } from '../model/driveErrors';
import { DriveFolderRef } from '../model/driveFolders';
import { PlanLine, awaitsProvisioning, planFolderOf } from '../model/driveSummary';
import { OrderDriveSummary, PlanDriveSummary } from '../model/types';
import useArtworkDriveService from './artworkDrive.service';
import { artworkDriveApi, artworkDriveKeys } from './artworkDriveApi';

/** A Drive read is not retried: a 404 or a 403 is an answer, not a fault. */
const driveRetry = false;

const PROVISIONING_POLL_MS = 5_000;
/** About two minutes of polling: a folder still missing then is a worker problem, not a delay. */
const PROVISIONING_POLLS = 24;

const SUMMARY_STALE_MS = 15_000;

const silent = () => undefined;

const provisioningPoll = (
  data: OrderDriveSummary | undefined,
  query: { state: { dataUpdateCount: number } },
) =>
  awaitsProvisioning(data) && query.state.dataUpdateCount < PROVISIONING_POLLS
    ? PROVISIONING_POLL_MS
    : false;

const FOLLOW_DEBOUNCE_MS = 1_000;

const startsWith = (key: QueryKey, prefix: QueryKey) =>
  prefix.every((part, index) => key[index] === part);

/**
 * Re-reads [target] once a burst of successful reads under any of [follow] settles: an order's folders follow its
 * bookings and decorations, which the order page re-reads after every change.
 */
const useFollowQueries = (target: QueryKey, follow: QueryKey[] | undefined) => {
  const queryClient = useQueryClient();
  const targetKey = JSON.stringify(target);
  const followKey = JSON.stringify(follow ?? []);

  useEffect(() => {
    const prefixes = JSON.parse(followKey) as QueryKey[];
    if (!prefixes.length) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      if (!prefixes.some((prefix) => startsWith(event.query.queryKey, prefix))) return;
      clearTimeout(timer);
      timer = setTimeout(
        () => queryClient.invalidateQueries({ queryKey: JSON.parse(targetKey) as QueryKey }),
        FOLLOW_DEBOUNCE_MS,
      );
    });

    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, [queryClient, targetKey, followKey]);
};

/**
 * The order's Drive summary. A 404 means Drive is off for this environment (or the order has no folders yet): the
 * screens then show what they showed before Drive, so the error is the signal, not a failure to report.
 */
export const useOrderDriveSummaryQuery = (
  orderId: string | undefined,
  /** Queries whose fresh reads mean the order changed, so its folders may have too. */
  follow?: QueryKey[],
) => {
  const { getOrderDriveSummary } = useArtworkDriveService();
  useFollowQueries(artworkDriveKeys.orderSummary(orderId ?? ''), follow);

  return artworkDriveApi.useFeatureQuery({
    queryKey: artworkDriveKeys.orderSummary(orderId ?? ''),
    queryFn: (signal) => getOrderDriveSummary(orderId as string, signal),
    enabled: Boolean(orderId),
    retry: driveRetry,
    staleTime: SUMMARY_STALE_MS,
    refetchInterval: provisioningPoll,
  });
};

/**
 * The Drive summaries of every order a list's rows belong to, by order id. An order Drive answers 404 for — Drive off,
 * or an order the viewer cannot see — is simply absent, and its rows keep their platform cells.
 */
export const useOrderDriveSummaries = (orderIds: string[]): Record<string, OrderDriveSummary> => {
  const { getOrderDriveSummary } = useArtworkDriveService();
  const results = useQueries({
    queries: orderIds.map((orderId) => ({
      queryKey: artworkDriveKeys.orderSummary(orderId),
      queryFn: ({ signal }: QueryFunctionContext) => getOrderDriveSummary(orderId, signal),
      retry: driveRetry,
      staleTime: SUMMARY_STALE_MS,
      refetchInterval: provisioningPoll,
    })),
  });

  // Folded into two fixed keys, as useBookingOperations does: the deps array keeps its length when the page does not.
  const idsKey = orderIds.join(',');
  const dataKey = results.map((result) => result.dataUpdatedAt).join(',');

  return useMemo(
    () =>
      orderIds.reduce<Record<string, OrderDriveSummary>>((summaries, orderId, index) => {
        const summary = results[index]?.data;
        if (summary) summaries[orderId] = summary;
        return summaries;
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey, dataKey],
  );
};

export const useDriveFolderAccessQuery = (
  orderId: string,
  folder: DriveFolderRef,
  enabled = true,
) => {
  const { listFolderAccess } = useArtworkDriveService();

  return artworkDriveApi.useFeatureQuery({
    queryKey: artworkDriveKeys.folderAccess(orderId, folder),
    queryFn: (signal) => listFolderAccess(orderId, folder, signal),
    enabled,
    retry: driveRetry,
    // The access panel shows its own refusal in place.
    onError: silent,
    staleTime: 0,
  });
};

/**
 * The plan's folders, listed live from Drive. With [lines], the plan lines a screen shows, a line still without a folder
 * re-reads it for a while: the worker makes a new line's folder seconds after the plan is saved.
 */
export const usePlanDriveSummaryQuery = (
  planId: string | undefined,
  enabled = true,
  lines?: PlanLine[],
) => {
  const { getPlanDriveSummary } = useArtworkDriveService();
  const query = artworkDriveApi.useFeatureQuery({
    queryKey: artworkDriveKeys.planSummary(planId ?? ''),
    queryFn: (signal) => getPlanDriveSummary(planId as string, signal),
    enabled: enabled && Boolean(planId),
    retry: driveRetry,
    staleTime: SUMMARY_STALE_MS,
    refetchInterval: (data: PlanDriveSummary | undefined, { state }) =>
      data &&
      lines?.some((line) => !planFolderOf(data, line)) &&
      state.dataUpdateCount < PROVISIONING_POLLS
        ? PROVISIONING_POLL_MS
        : false,
  });

  return { ...query, driveUnavailable: isDriveUnavailable(query.error) };
};

/**
 * Who besides the author reaches the plan's folders — readable by the plan's author and staff only. Its refusals are
 * shown in place, never as a toast: a 403 here also says Drive is on for a viewer who may not see the list.
 */
export const usePlanDriveAccessQuery = (planId: string | undefined, enabled = true) => {
  const { listPlanAccess } = useArtworkDriveService();

  return artworkDriveApi.useFeatureQuery({
    queryKey: artworkDriveKeys.planAccess(planId ?? ''),
    queryFn: (signal) => listPlanAccess(planId as string, signal),
    enabled: enabled && Boolean(planId),
    retry: driveRetry,
    onError: silent,
    staleTime: 60_000,
  });
};

/**
 * Whether the plan has Drive folders, without listing them: the access list answers 404 when Drive is off or the plan
 * has no folder yet, 403 (before any Google call) to a viewer who is not the author or staff, and lists for the rest.
 * Listing the files reads Drive folder by folder, so that waits until someone opens the plan's Drive dialog.
 */
export const usePlanDriveAvailability = (planId: string | undefined, enabled = true) => {
  const access = usePlanDriveAccessQuery(planId, enabled);

  return {
    available: access.isSuccess || getErrorKind(access.error) === 'forbidden',
    canSeeAccess: access.isSuccess,
  };
};
